import {_, classNames, React, utils, stringify, pluralize} from '@api'
import {UNASSIGNED} from '@api/graphql'
import {Intent} from '@blueprintjs/core'
import {GroupCountBadge, GroupCountBadges, ISwatchHoverItem, GroupBySpecInfoCard, ISubgroupHoverItem, GroupByGroupHeroCards, GroupSwatches, BuilderCardStore, IContextMenuItems, GroupByGroupContextMenu, GroupBySwatchesStore, AssortmentRootStore, AssortmentCardStore, CatalogItemsCardViewStore} from '@design-board'
import {CardActions} from '@material-ui/core'
import {EMPTY_STRING_VALUE, NULL_KEY_VALUE} from '@modules'
import {CATALOG_ID_SEPARATOR, CatalogPerspectiveConfig, DragDropEventHandlers, GroupByGrouping, GroupTitleConfig, ICatalogFieldConfig, IDragDropElement, IIndividualGroupSettings, OFFLINE_ID, onSiteError, site, StackView, StoresInstance, ui, useCatalog} from '@stores'
import {onTouchDoubleTap} from 'centric-ui-components'
import {action, autorun, computed, observable, reaction, runInAction, toJS, when} from 'mobx'
import {computedFn} from 'mobx-utils'
import {Fragment, useContext} from 'react'
import {ChildGroupsStore, GroupByGroupHeroCardsStore, GroupByGroupProps, GroupInfoCardMoreMenuButton, GroupSubtitles, GroupTitle} from './'
import {VipLocalStore} from '@modules'
import * as css2 from './GroupByContainer.css'
import * as styles from './GroupByGroup.css'
import * as css from './shared.css'
import {useGroupByDropdownStore} from '@modules'

export const useGrouping = () => {
  const groupByGroup = useGroupByGroupStore(),
    groupByDropdown = useGroupByDropdownStore(),
    {groupBy} = useCatalog()

  return groupByGroup?.grouping || groupByDropdown?.grouping || _.first(groupBy.hierarchyGroups) || groupBy.catalogItemsGroup
}

export const GroupByGroupStoreContext = React.createContext<GroupByGroupStore>(undefined)
export const useGroupByGroupStore = () => useContext(GroupByGroupStoreContext)

export type ContextualGroupHoverItem = ISubgroupHoverItem | ISwatchHoverItem
export type GroupByGroup_TargetExtra = {
  ids: string[],
  grouping: GroupByGrouping,
  store: GroupByGroupStore,
  field?: ICatalogFieldConfig
}
/**
 * Instantiated 1-1 with <GroupByGroup />
 */
export class GroupByGroupStore extends VipLocalStore implements IContextMenuItems {
  @observable.ref swatchStore: GroupBySwatchesStore
  @observable.ref assortmentRoot: AssortmentRootStore

  /**
   * When copying to clipboard show a checkbox for a moment in the icon
   */
  @observable copyDbString = false
  @action onCopyGroupByQueryToClipboard = () => {
    const {catalog} = this
    navigator.clipboard.writeText(`GET ${catalog.index}/_search\n${JSON.stringify(this.childGroupStore.mgr.queryVariables ?? this.childGroupStore.productQueryVariables, null, 2)}`)
    this.copyDbString = true
    setTimeout(() => this.copyDbString = false, 2000)
  }

  @action onCopyGroupByRootQueryToClipboard = () => {
    const {catalog} = this
    navigator.clipboard.writeText(`GET ${catalog.index}/_search\n${JSON.stringify(this.grouping.groupBy.viewStore.mgr.queryVariables, null, 2)}`)
    this.copyDbString = true
    setTimeout(() => this.copyDbString = false, 2000)
  }

  @action onCopyStoreToWindow = () => {
    window['groupByGroupStore'] = this
    console.debug(`Copied store for '${this.props.info.title}' into window.groupByGroupStore and your clipboard`)
    navigator.clipboard.writeText(`window.groupByGroupStore`)
  }

  get hasExpandedCatalogItems() { return this.levelOfDetail === 1 || this.hasInlineExpandedCatalogItems}

  @computed get shouldWaitToLoadProducts() {
    const {indexInGroupsInOrder, grouping: {isExpandedAll}} = this
    if (indexInGroupsInOrder <= 0 || !isExpandedAll) return false

    const productStoreToMyLeft = this.grouping.groupsInOrder[indexInGroupsInOrder - 1].productStore
    return productStoreToMyLeft?.initialDataLoaded !== true
  }

  //@observable.ref location: RectReadOnly

  /**
   * Used to persist results between expand/collapse/sort of this group
   * {@see GroupByGroupHeroCards}
   */
  subgroupItems = observable.map<string, Array<any>>({}, {deep: false})

  /**
   * {@see GroupSwatches}
   */
  @observable.ref swatchData: { [field: string]: Array<any> } = {}
  subgroupStore: GroupByGroupHeroCardsStore
  @observable.ref cardViewStore?: CatalogItemsCardViewStore

  @computed get fullTitle() {
    const {strings, grouping: {mySubtitleFields}, props: {info: {title, keys}}} = this

    return [title, ...mySubtitleFields.map(f => keys[f.field] === null ? strings.unassigned : keys[f.field] === '' ? strings.noValue : keys[f.field])].join(' + ')
  }

  @computed get hierarchyIncludingSelf() {
    const {props: {level}, groupBy} = this
    return groupBy.hierarchyGroups.slice(0, level + 1)
  }

  @computed get mySubtitleFieldsWithValues() {
    const {strings, catalog, vlp, props: {info}, grouping: {mySubtitleFields}} = this
    return mySubtitleFields.map((subtitleField, i) => {
      const v = subtitleField.valueFor ? subtitleField.valueFor(info)?.label : info.keys[subtitleField.field]

      let label

      if (vlp?.hierarchy?.hasVlpHierarchy) {
        const r = vlp.hierarchy.parseHierarchyGroup(subtitleField.field)
        if (r) {
          label = v === '' ? strings.noValue : v === null ? strings.unassigned : vlp.hierarchy.itemOfType(r.type, v, null)?.name ?? strings.noValue
        }
      }

      if (!label) {
        label = v === '' ? strings.noValue : v === null ? strings.unassigned : catalog.attrValueLabelForFieldName({v, field: subtitleField.field})
      }

      return {...subtitleField, value: v, label}
    })
  }

  @computed get groupDefinition() {
    return this.grouping?.fieldDefinition?.group
  }

  @computed get indexInGroupsInOrder() {
    const {grouping: {groupsInOrder}} = this
    return groupsInOrder.findIndex(g => g.store === this)
  }

  // @computed get cardRow() {
  //   const {location, grouping: {groupsInOrder}, indexInGroupsInOrder} = this
  //   if (indexInGroupsInOrder === 0) {
  //     return 0
  //   } else {
  //     const previousGroup = groupsInOrder[indexInGroupsInOrder - 1]
  //     if (previousGroup.store.location.top === location.top) {
  //       return previousGroup.store.cardRow
  //     } else {
  //       return previousGroup.store.cardRow + 1
  //     }
  //   }
  // }
  //
  // @computed get cardColumn() {
  //   const {location, grouping: {groupsInOrder}, indexInGroupsInOrder} = this
  //   if (indexInGroupsInOrder === 0) {
  //     return 0
  //   } else {
  //     const previousGroup = groupsInOrder[indexInGroupsInOrder - 1]
  //     if (previousGroup.store.location.top === location.top) {
  //       return previousGroup.store.cardColumn + 1
  //     } else {
  //       return 0
  //     }
  //   }
  // }
  //
  // @computed get locationInRow(): 'left' | 'middle' | 'right' {
  //   const {location, grouping: {groupsInOrder}, indexInGroupsInOrder} = this
  //   if (indexInGroupsInOrder + 1 == groupsInOrder.length) {
  //     return 'right'
  //   } else if (indexInGroupsInOrder === 0) {
  //     return 'left'
  //   } else {
  //     if (groupsInOrder[indexInGroupsInOrder - 1].store.location.top < location.top) {
  //       return 'left'
  //     } else if (groupsInOrder[indexInGroupsInOrder + 1].store.location.top > location.top) {
  //       return 'right'
  //     } else {
  //       return 'middle'
  //     }
  //   }
  // }

  @computed get totalCount() {
    const {props: {info: {offline_count, count}}} = this
    return (offline_count ?? 0) + (count ?? 0)
  }

  @computed get esInfo() {
    const {es, props: {info: {aggs, keys}}, grouping: {esField, esFieldRoot, fieldDefinition}} = this

    if (aggs?.esItem) {
      const keyValue = _.get(keys, esFieldRoot)

      let potentialResults = _.flatMap((aggs?.esItem?.hits?.hits ?? []).map(({_source}) => es.readField(_source, esFieldRoot)?.val))

      let r = potentialResults.filter(v => _.get(v, fieldDefinition.group?.field ?? 'name') === keyValue)

      if (_.isArray(r)) return _.first(r)

      return r
    }

    return null
  }

  @computed get rootStore() {
    return this.parentStore == null ? this : this.parentStore.rootStore
  }

  @computed get showGroupOfflineItemsOnly() {
    return this.offlineItems.length > 0 && (this.settings.showOnlyOfflineItems || this.parentStore?.showGroupOfflineItemsOnly)
  }

  @computed get pathIncludingSelf() {
    const {props: {path, group, groupName}, grouping, catalog: {groupBy: {hierarchyGroups}}} = this
    return [
      ...path.filter((p, i) => hierarchyGroups[i]).map((p, i) => ({group: p, groupName: hierarchyGroups[i].groupName, grouping: hierarchyGroups[i]})),
      {group, groupName, grouping}
    ]
  }

  @computed get columns() {
    const {groupBy, catalog: {settings: {groupBy: {flatModeColumns}}}, settings, settings: {columnView}, childGroupStore, props: {level}} = this, {groupBy: {hierarchy}} = this,
      childColumns = settings?.childColumns ? settings.childColumns : groupBy.individualGroupSettings.childColumns
    return columnView ? Math.max(childGroupStore.mgr.groupCount, childGroupStore.mgr.data.length) : /*level + 2 != hierarchy.length ? 1 :  childColumns */ Math.min(flatModeColumns, childGroupStore.mgr.data.length)
  }

  @computed get pathIncludingSelfKeys() {
    const {props: {path, group}} = this
    return [
      ...path,
      group
    ]
  }

  @computed get showEmptyMessage() {
    const {catalog, props: {info: {count, offline_count}}, grouping: {groupName}} = this,
      {settings: {groupBy: {showEmptyGroups}}, groupBy: {hierarchy}} = catalog
    return ((count + offline_count) == 0) && (!showEmptyGroups || _.last(hierarchy) === groupName)
  }

  /*
    We only have viewMode as a global to the catalog:  flat or card.  Spec groups with other specs inside of them do not look good or use space well
   */
  @computed get viewMode() {
    return this.catalog.settings.viewMode
    //return this.hasChildGroup ? 'flat' : this.catalog.settings.viewMode
  }

  @computed get ddHandler() {
    return this.dragDrop.ddHandlers.get(this.storeNodeId)
  }

  @computed get dragDropState() {
    const {ddHandler, storeNodeId} = this
    return ddHandler?.state
  }

  @computed get success() { return this.dragDropState?.status == 'success'}

  @observable everExpanded = false

  /**
   * Catalog item results by group and load more index
   */
  catalogItemsByGroup = observable.map<string, Array<any>>({}, {deep: false})

  constructor(stores: StoresInstance,public props: GroupByGroupProps & {assortmentCard?: AssortmentCardStore}) {
    super(stores)
    this.childGroupStore = new ChildGroupsStore(stores, this)
    this.onTouchDoubleTap = onTouchDoubleTap(this.onDoubleClick)
    this.everExpanded = this.isExpanded
  }

  get contextMenuItems() {
    return (
      <GroupByGroupStoreContext.Provider value={this}>
        <GroupByGroupContextMenu/>
      </GroupByGroupStoreContext.Provider>
    )
  }

  registerEffects = () => {
    const toDispose = [
      autorun(() => this.storeNode && (this.storeNode.store = this)),
      autorun(() => this.everExpanded = this.everExpanded || this.isExpanded),
      reaction(() => this.catalog.settings.levelOfDetail, () => {
        this.settings.levelOfDetail = undefined
      })
    ]

    return () => toDispose.forEach(d => d())
  }

  @computed get storeNode() {
    const {grouping, storeNodeId} = this
    return storeNodeId && grouping.nodes.get(storeNodeId)
  }

  @computed get allChildrenLoaded() {
    const {isExpanded, childGroupStore: {childGroups}} = this
    const r = !isExpanded || (childGroups.length && childGroups.every(g => g?.productStore?.initialDataLoaded))
    return r
  }

  @computed get specStack(): any {
    const {props: {info}} = this
    return [{...info}]
  }

  @computed get groupBySettings() { return this.catalog.groupBy.settings}

  @computed get isFlat() { return this.groupView === 'flat' }

  @computed get isCard() { return this.groupView === 'card' }

  get showCardToolbar() { return false }

  @computed get isDragOver() {
    const {dragDrop: {dropItem}, props: {info, path}} = this
    return dropItem && dropItem.id == info.key.toString() && dropItem.path == path
  }

  @computed get isCustomAttribute() {
    const {grouping} = this
    return !grouping.isSpec && grouping?.def != null
  }

  @computed get groupHeader() { return <span className={css.groupTitle}>{this.groupTitle}</span> }

  @computed get specInfoCardExtra() {

    const {
      isUnassigned, items, groupBySettings: {groupView}, props: {
        level, group, path
      }, specStack
    } = this

    return (
      <Fragment>
        {/*{showSwatches && <CardProductSpecSwatches className={css.swatches} products={items} spec={groupName}*/}
        {/*                                      onlySpecs={viewByOptions.map(s => s.value)*/}
        {/*                                            .filter(s => !hierarchy.slice(0, level).find(h => h == s)) // Remove items above us in the hierarchy*/}
        {/*                                            .filter(s => isExpanded || hierarchy.length == 1 || level + 1 == hierarchy.length || hierarchy.slice(level).find(h => h == s)) // If there are more specs in the hierarchy ONLY show swatches for those specs*/}
        {/*                                          }/>}*/}

        {groupView == 'card' && <CardActions className={classNames(css2.actions)}>
			<GroupInfoCardMoreMenuButton group={group} path={path} specStack={specStack} isUnassigned={isUnassigned} items={items} level={level}/>
		</CardActions>}
      </Fragment>)
  }

  //renderGroupHeader=(groupHeader:string)=> null

  @computed get showSwatches() {
    return this.groupBySettings.showSwatches === true
  }

  @computed get groupLabel() {
    const {props: {info: {title}}} = this
    return !title || title == '--'
           ? 'Unassigned'
           : title
  }

  @computed get groupTitleLabel(): string {
    const {props: {groupName}, grouping: {groupTitle, def}} = this
    return pluralize((groupTitle || def?.displayName?.plural) ?? groupName)
  }

  get title() { return this.groupTitle}

  @computed
  get groupTitle() {
    const {grouping: {fieldDefinition}, groupTitleLabel, catalog: {labelForSection, catalogConfig}} = this

    return (
      <Fragment>
        {catalogConfig?.features?.stackedCards && fieldDefinition && <span className={styles.titleSection}>{labelForSection(fieldDefinition?.section, fieldDefinition)}:</span>}
        {`${groupTitleLabel}`}
      </Fragment>)
  }

  @computed get customAttribute() {
    return this.grouping.isCustomAttribute
  }

  @computed get storeNodeId() {
    const {grouping, props: {path, group}} = this
    if (!grouping) return null
    return grouping.pathLookup(group, path)
  }

  @computed get parentGrouping() {
    const {props: {level}} = this,
      {catalog: {groupBy: {groupingByName, hierarchy}}} = this
    if (level == 0) {
      return null
    }

    return groupingByName.get(hierarchy[level - 1])
  }

  @computed get isSpec() {
    return this.grouping.isSpec
  }

  @computed get headerHeight() {
    const {catalog, infoCardProps: {data: {subtitle1}, hideImages}, isSpec, isCard, catalog: {showGroupHeader}} = this
    return Math.max(
      44,
      ((showGroupHeader ? 4 : 0) + (subtitle1 ? 6 : 0) + (isSpec && !hideImages ? 48 : 44)) * (!isCard ? 1 : Math.min(catalog.cardSizeSliderAsMultiplier, 1.3)))
  }

  @computed get headerTop() {
    const {props: {level}, parentStore, groupBy} = this, {settings: {groupView}} = groupBy
    if (level === 0) {
      return 0
    } else {
      return parentStore?.headerTop + (parentStore?.isCard ? 0 : parentStore?.headerHeight) ?? 0
    }
    // _.range(hierarchy.length).reduce((acc, i) => ({
    //   ...acc,
    //   [`--header-${i}-height`]: `${39 + () + (showGroupHeader ? 4 : 0)}px`
    // }), {})
  }

  @computed get parentStore() {
    const {props: {path}, parentGrouping} = this
    if (!parentGrouping) {
      return null
    }

    const parentNode = parentGrouping.nodes.get(parentGrouping.pathLookup(_.last(path), path.slice(0, path.length - 1)))
    return parentNode && parentNode.store
  }

  @computed get loading() {
    const {props: {loading}, childGroupStore, storeNode} = this
    return loading || (childGroupStore?.mgr?.loading && !childGroupStore?.mgr?.groupCount) || (storeNode?.productStore?.isLoadingMore && !storeNode.productStore.everLoadedData)
  }

  @computed get levelOfDetail() {
    const {props: {level}, settings, parentStore, catalog: {settings: {levelOfDetail}}} = this
    return _.isNumber(settings?.levelOfDetail) ? settings.levelOfDetail : (level == 0 ? levelOfDetail : parentStore instanceof GroupByGroupStore ? parentStore?.levelOfDetail ?? levelOfDetail : levelOfDetail)
  }

  @computed get fullPath() {
    return this.pathIncludingSelf.reduce((acc, {group}) => {
      const p = utils.isJson(group) ? JSON.parse(group) : group
      return _.assign(acc, p)
    }, {})
  }

  @computed get pathLookup() {
    return stringify(this.fullPath)
  }

  @computed get settings() {
    const r = this.grouping.settingsByGroup.get(this.pathLookup) ?? this.groupBy.individualGroupSettings
    return r
  }

  @action updateSettings = (settings: Partial<IIndividualGroupSettings>) => {
    //console.debug(`updateSettings(${JSON.stringify(settings)}) - ${pathLookup}`)

    const {groupBy, pathLookup, grouping: {settingsByGroup}} = this

    if (!settingsByGroup.has(pathLookup)) {
      settingsByGroup.set(pathLookup, _.cloneDeep(groupBy.individualGroupSettings))
    }

    Object.assign(this.settings, settings)
  }

  @action onApplyColumnsToAllGroups = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const {groupBy, columns} = this
    groupBy.individualGroupSettings.childColumns = columns

    groupBy.hierarchyGroups.forEach(g => {
      Array.from(g.settingsByGroup.values()).forEach(s => s.childColumns = columns)
    })
  }

  @action onChangeColumns = (e, c: number) => {
    this.updateSettings({childColumns: c ?? this.groupBy.individualGroupSettings.childColumns})
  }

  @action onToggleColumnView = () => {
    const {settings} = this
    this.updateSettings({columnView: !settings.columnView})
  }

  @action onToggleFullWidth = () => {
    const {settings} = this
    this.updateSettings({fullWidth: !settings.fullWidth})
    const {parentStore} = this
    if (parentStore) {
      if (settings.fullWidth) {
        !parentStore.settings.fullWidth && parentStore.onToggleFullWidth()
      } else {
        parentStore.settings.fullWidth && parentStore.childGroupStore.mgr.groupCount == 1 && parentStore.onToggleFullWidth()
      }
    }
  }

  @action onToggleSingleLine = () => {
    this.updateSettings({singleLine: !this.settings.singleLine})
  }

  @action toggleInfoPanel = () => {
    const {settings} = this
    this.updateSettings({infoPanelActive: !settings.infoPanelActive})
    return settings.infoPanelActive
  }

  @computed get dragDropLoading() {
    return this.dragDropState?.status === 'loading'
  }

  @computed get showSuccess() {
    return this.dragDropState?.status == 'success'
  }

  @computed get ddEventsNoDragStart() {
    return _.omit(this.groupDragDropEvents.events, ['onDragStart'])
  }

  @computed get showDropIndicator() {
    const {groupDragDropEvents} = this
    return groupDragDropEvents?.showDropIndicator && groupDragDropEvents.isDropAllowed !== null
  }

  @computed get groupDragDropEvents(): Partial<DragDropEventHandlers> {
    const {catalog, storeNodeId, parentStore, grouping, grouping: {fieldDefinition}, pathIncludingSelfKeys, props: {info: {keys}, path}} = this,
      targetExtra: GroupByGroup_TargetExtra = {
        get ids() { return this.storeNode?.productStore?.catalogItemIdsArray ?? []},
        grouping,
        store: this
      }
    if (fieldDefinition.group?.add !== false) {
      if (parentStore) {
        const {cardData: {key: parentId}} = parentStore
        return new DragDropEventHandlers(catalog, {id: storeNodeId, parentId, target: 'GroupHeader', targetExtra})
      } else {
        return new DragDropEventHandlers(catalog, {id: storeNodeId, target: 'GroupHeader', targetExtra})
      }
    }
    return {}
  }

  @computed get infoCardProps(): any {
    const {
        items, success, childGroupStore, groupDefinition, grouping,
        isSelected, size, cardData, busy, showQRCode, groupView, isSpec,
        props: {
          info, level,
          loading
        },
        setInfoCardRef, specInfoCardExtra,
        toggleInfoPanel, settings: {infoPanelActive}
      } = this,
      {catalog: {dialogs: {showQrCodes}}} = this
    return {
      groupStore: this,
      ref: setInfoCardRef,
      items,
      key: info.key.toString(),
      hideImages: !isSpec || groupDefinition?.hideSpecImage || grouping?.fieldDefinition?.renderer === 'text',
      showQRCode: showQRCode != null ? showQRCode : showQrCodes,
      isSelected: isSelected && level == 0,
      className: classNames(css.rowCard),
      groupView: groupView,
      loading: busy || loading || childGroupStore?.mgr?.loading || this.groupDragDropEvents?.state?.status === 'loading',
      cardSize: size,
      data: cardData,
      titleCount: <GroupCountBadges/>,
      offlineItemsCount: <GroupCountBadge offline/>,
      extra: specInfoCardExtra,
      success,
      toggleInfoPanel: level == 0 ? toggleInfoPanel : undefined, infoPanelActive,
      productCount: info.count, level,
      stores: this.stores
    }
  }

  @action selectNone = (e: KeyboardEvent) => {
    const {catalog: {idFor, selection: {selectedIds}}} = this,
      {items, pathLookup} = this

    if (!e.shiftKey) {
      selectedIds.clear()
    } else {
      items.forEach((item) => {
        const id = `${pathLookup}.${idFor(item)}`
        selectedIds.has(id) && selectedIds.delete(id)
      })
    }
  }

  @action selectAll = (e: KeyboardEvent) => {
    const {catalog: {selection: {selectedIds}, idFor}} = this

    try {
      const {items, pathLookup} = this

      if (!e.shiftKey) {
        selectedIds.clear()
      }
      items.forEach((item) => selectedIds.add(`${pathLookup}.${idFor(item)}`))
    } catch (err) {
      console.error(err)
    }
  }

  @computed get queryKey() {
    const {props: {info: {key}, assortmentCard}} = this
    return `${assortmentCard ? `${assortmentCard.id}-` : ''}${key}`
  }

  @computed get groupView() {
    const {grouping: {isSpec, allowCardMode, isLeaf}, groupBySettings: {groupView}, isExpanded} = this
    return isSpec && allowCardMode && (!isExpanded || isLeaf) ? this.groupBySettings.groupView : 'flat'
  }

  /**
   * While loading we may know that we have 100 children items, but our child group is colors.
   * Until we get those items back by color from the server we want to say:
   *  "100 items"
   * But after:
   *  "12 Colors"
   *
   * Note that above is only for flat view, in card view we always want to show (###) with the product count (and eventually swatches)
   */
  @computed get titleCount() {
    const {
      size, groupView,
      props: {
        info, level
      },
      offlineItems
    } = this

    const childrenCount = info.count

    let r = (groupView == 'card' && size == 'medium') || level > 0 ? childrenCount
                                                                   : childrenCount != null ? childrenCount : '...'//} ${catalog.boardConfig.itemName}${childrenCount == 1 ? '' : 's'}`
    r = _.isString(r) ? parseInt(r) : r >= 0 ? r : 0

    return r + offlineItems?.length
  }

  @computed get items(): any[] {
    const {hasChildGroup, storeNode} = this
    if (!hasChildGroup) {
      return storeNode?.productStore?.catalogItemsArray ?? []
    }
  }

  @computed get itemsIdArray(): string[] {
    const {catalog, hasChildGroup, storeNode, pathLookup} = this
    if (!hasChildGroup) {
      return storeNode?.productStore?.catalogItemsArray.map(v => `${pathLookup}.${catalog.idFor(v)}`) ?? []
    }
  }

  onClickProduct = (e: React.MouseEvent, id: string) => {
    this.catalog.selection.onClickItem(e, id, this.itemsIdArray, this.settings.stackView)
  }

  infoCardRef: GroupBySpecInfoCard

  onTouchDoubleTap
  @observable confirmDialog: { dragItem: IDragDropElement, title, intent?: Intent, content?: React.ReactNode, data?: any } = null
  @observable busy = false
  @observable preload = false

  get cardData() {
    const {props: {info}, grouping: {fieldDefinition, subtitleFields}, stores} = this
    if (!info) return null

    let r = {
      ...(this.esInfo ?? {}),
      __groupInfo: info,
      title: <GroupTitle/>,
      subtitle1: subtitleFields.every(st => st.valueFor && !st.valueFor(info)?.label) ? null : <GroupSubtitles/>
    }

    if (fieldDefinition?.group?.custom?.imagesFor) {
      try {
        r.images = fieldDefinition.group.custom.imagesFor(this, stores)
      }
      catch (err) {
        console.error(err)
      }
    }

    return r
  }

  @computed get size() {
    const {catalog: {groupBy: {settings: {compact}}}} = this, {props: {level}} = this

    return compact ? 'small'
                   : level == 0 ? 'medium' : 'medium' // !isExpanded ? 'medium' : 'large'
  }

  @computed get isExpanded() {
    const {grouping, props: {path, group, level}} = this, {groupBy: {settings: {expandAllLevel}}} = this

    //if (isVLPView && level > 0) return true

    return (grouping && grouping.isExpanded(group, path))
  }

  @computed get showQRCode() {
    const {grouping, props: {path, group}} = this

    return grouping && grouping.isShowQrCode(group, path)
  }

  @computed get isSelected() {
    const {grouping, props: {group, path}} = this
    return grouping && grouping.isSelected(group, path)
  }

  @computed get termLookup() {
    const {props: {info: {id, key}}} = this
    let lookup = key
    if (utils.isJson(key)) {
      lookup = JSON.parse(key)
    }

    return lookup
  }

  @computed get termValue() {
    return this.termLookup[this.props.groupName]
  }

  @computed get myMustTerms() {
    const {catalog, termValue, props: {mustTerms, info: {id, key, keys}, groupName}, grouping: {def, esField, fieldDefinition, groupsClauseFor, isNestedField, sectionConfig}} = this

    let r

    if (fieldDefinition?.agg == 'exists') {
      if (id === 'true') {
        r = [groupsClauseFor({})?.filter]
      }
      else {
        const existsField = catalog._fieldByName[esField]
        const mustNot = {
          must_not: {
            exists: {
              field: esField
            }
          }
        }

        r = existsField?.def?.type === 'String' ?
          [
            {
              bool: {
                minimum_should_match: 1,
                should: [
                  {
                    bool: mustNot
                  },
                  {
                    term: {[esField]: ''}
                  }
                ]
              }
            }
          ] : [{bool: {...mustNot}}]
      }

      r = sectionConfig?.viewOptions?.field ? [
        {
          nested: {
            path: sectionConfig.path,
            query: {
              bool: {
                filter: r
              }
            }
          }
        }
      ] : r
    } else if (def?.type == 'Boolean') {
      if (termValue === null) { return [] }

      const term = [{term: {[esField]: !termValue || id[groupName] === false ? false : true}}]

      r = sectionConfig?.viewOptions?.field ? [
        {
          nested: {
            path: sectionConfig.path, query: {
              bool: {
                filter: term
              }
            }
          }
        }
      ] : term

      /*
      // If we want to group false and unassigned together:
      return {
        bool: {
          minimum_should_match: 1,
          should: [
            { term: { [esField]: false} },
            {
              bool: {
                must_not: {exists: {field: esField}}
              }
            }
          ]
        }
      }*/
    } else {
      if (!keys) {
        r = !_.isEmpty(termValue) ? [
          {
            [_.isArray(termValue) ? 'terms' : 'term']: {[esField]: termValue}
          }
        ] : []

        r = sectionConfig?.viewOptions?.field ? [
          {
            nested: {
              path: sectionConfig.path, query: {
                bool: {
                  filter: r
                }
              }
            }
          }
        ] : r
      } else {
        const {catalog: {_fieldByName, esFieldFor}} = this
        r = Object.keys(keys).map(k => {
            if (catalog._fieldByName[k]?.agg == "exists") {
              return null
            } else {
              const v = keys[k],
                field = _fieldByName[k],
                sectionConfig = catalog.sectionConfigFor(field?.section),
                term = field && v !== null ? ({[_.isArray(keys[k]) ? 'terms' : 'term']: {[esFieldFor(field, true, false)]: keys[k]}}) : null

              return !term ? null
                           : sectionConfig?.viewOptions?.field ? {nested: {path: sectionConfig.path, query: {bool: {filter: [term]}}}}
                                                               : term
            }
          })
          .filter(v => v !== null)
      }
    }

    return r

    // !isNestedField || (mustTerms ?? []).some(t => t?.nested?.path === sectionConfig.path) ? r : [{
    //   nested: {
    //     path: sectionConfig.path,
    //     query: {
    //       bool: {filter: r}
    //     }
    //   }
    // }]
  }

  @computed get mustTerms() {
    const {es, props: {mustTerms, assortmentCard}, myMustTerms} = this

    return es.combineNestedTerms([
      ...(mustTerms || []),
      ...myMustTerms,
      ...(assortmentCard?.mustTerms ?? [])
    ].filter(_.identity))
  }

  @computed get myMustNotTerms() {
    const {catalog, termValue, props: {info: {id, key, keys}}, grouping: {isCatalogItemsGrouping, groupName, def, esField, fieldDefinition}} = this

    if (fieldDefinition?.agg == 'exists') {
      return []
    }

    const {catalog: {attrFieldFor, esFieldFor, _fieldByName}} = this

    if (!keys) {
      return (termValue === null) ? [
        {
          exists: {field: def ? attrFieldFor(def, fieldDefinition?.keyword) : groupName}
        }
      ] : []
    } else {
      //return (key === NULL_KEY_VALUE || key === UNASSIGNED) ? [
      //         {
      //           exists: {field: def ? attrFieldFor(def, keyword) : groupName}
      //         }] : []
      return Object.keys(keys).map(k => {
          if (catalog._fieldByName[k]?.agg == "exists") {
            return null
          } else {
            const v = keys[k]

            return v === null ? ({exists: {field: esFieldFor(_fieldByName[k], !isCatalogItemsGrouping)}}) : null
          }
        })
        .filter(v => v !== null)
    }
  }

  @computed get mustNotTerms() {
    const {myMustNotTerms, props: {mustNotTerms, assortmentCard}} = this

    return [
      ...(mustNotTerms || []),
      ...myMustNotTerms
    ].filter(_.identity)
  }

  // endregion

  @computed get grouping() {
    const {props: {groupName}, catalog: {groupBy: {groupingByName}}} = this
    const r = groupingByName.get(groupName)

    return r
  }

  // region Child Group (multi-level hierarchy)

  childGroupStore: ChildGroupsStore

  /**
   * Avoid loading groups that are offscreen // causes jitter, now we only load groups in batches - except when there's subtitles with custom sort
   */
  @observable everVisible = false

  @computed get hasChildGroup() {
    const {props: {level}, catalog: {groupBy: {hierarchy}}} = this

    return level + 1 < hierarchy.length
  }

  // endregion

  setSelectedTimeout

  @action selectGroup = () => {
    const {props: {group, info}, pathIncludingSelfKeys, grouping, catalog: {selection, groupBy: {settings: {groupView}}}} = this

    grouping?.onClickGroup(group, {buttons: 1, path: pathIncludingSelfKeys})
  }

  @action onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const {props: {group, info}, pathIncludingSelfKeys, grouping, catalog: {selection, groupBy: {settings: {groupView}}}} = this
    const {shiftKey, metaKey, altKey, ctrlKey} = e

    const el = e.target as HTMLElement
    // Clicked on the card?
    if (el.closest('[data-type="GroupByGroupHeader"]')) {
      e.stopPropagation()
      const buttons = e['buttons'] ? e['buttons'] : 1
      // Avoid triggering this behavior if they double click
      this.setSelectedTimeout = setTimeout(() => runInAction(`Set selected group to ${info.key} (${group})`, () => {
        buttons === 1 && grouping?.onClickGroup(group, {buttons, path: pathIncludingSelfKeys, shiftKey, metaKey, ctrlKey, altKey})
      }), 200)
    } else if (groupView == 'card') {
      // Clicked in whitespace
      selection.clear()
    }
  }

  scrollToSpecAfterAnimationCompletes = _.debounce(() => {
    const {groupBy} = this
    // if (this.isCard && this.grouping.hasSubGroup && !groupBy.settings.columnView) {
    //   this.ref?.scrollIntoView({behavior: 'smooth', block: this.isExpanded ? 'start' : 'start', inline: 'start'})
    // }

    /*const {props: {catalog: {groupBy: {showExpandedFirst}}, path}, groupBySpec} = this
    if (showExpandedFirst && (this.isExpanded || (!this.isExpanded && groupBySpec.areAllCollapsed(path)))) {
      this._cancelHandle = setInterval(() => {
        if (!this.props.isParentAnimating) {
          clearInterval(this._cancelHandle)
          this._cancelHandle = null
          scrollIntoView(this.node, {
            behavior: instructions => {
              if (instructions && !_.isEmpty(instructions)) {
                const [{el, top}] = instructions
                el.scrollTop               = top - 8
              }
            }, block: this.isExpanded ? 'start' : 'center', scrollMode: 'if-needed'
          })
          if(!avoidAllSelect) {
            const {props: {group, path, info}} = this
            runInAction(`Select only ${info.name} ${[group, ...path].join(' → ')}`, () => {
              this.groupBySpec.selected.clear()
              this.groupBySpec.setSelected(group, path, true)
            })
          }
        }
      }, 300)
    }*/
  }, 200)

  // region Stack View
  @observable previousStackView ?: StackView
  /**
   * If the user clicks from style A -> C and then clicks on C again I want to toggle back to A
   * @param view
   */
  @action onClickStackView = (clickedView: StackView) => () => {
    const {settings: {stackView: currentView}} = this
    this.previousStackView = clickedView == currentView ? clickedView : currentView
    this.updateSettings({stackView: clickedView})
  }

  // endregion

  @action toggleLevelOfDetail = () => {
    this.updateSettings({levelOfDetail: this.levelOfDetail == 1 ? 0 : 1})
  }

  @action toggleOrientation = () => this.updateSettings({orientation: this.settings.orientation == 'horizontal' ? 'vertical' : 'horizontal'})

  @action collapse = () => {
    const {isExpanded, toggleExpanded} = this
    isExpanded && toggleExpanded()
  }

  @action expand = () => {
    const {isExpanded, toggleExpanded} = this
    !isExpanded && toggleExpanded()
  }
  @action toggleExpanded = () => {
    const {props: {path, group}, grouping} = this
    grouping.toggleExpanded(group, path)
  }

  onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    let {setSelectedTimeout, toggleExpanded} = this

    setSelectedTimeout && clearTimeout(setSelectedTimeout)
    this.setSelectedTimeout = null

    toggleExpanded()

    e.stopPropagation()
    e.preventDefault()
    this.scrollToSpecAfterAnimationCompletes()
  }

  preloadTimer
  @observable mouseEnter = false
  @observable mouseHasEntered = false

  @action onMouseEnter = () => {
    this.mouseEnter = true
    this.mouseHasEntered = true

    const {preload, isExpanded} = this
    if (!isExpanded && !preload) {
      this.preloadTimer = setTimeout(() => {
        this.preloadTimer = null
        this.preload = true
      }, ui.settings.PRELOAD_HOVER_TIME)
    }
  }

  @action onMouseLeave = () => {
    this.mouseEnter = false
    const {preloadTimer} = this
    if (preloadTimer) {
      clearTimeout(preloadTimer)
      this.preloadTimer = null
    }
  }

  // @action specCard_onDragEnter = (e: React.MouseEvent) => {
  //   const {props: {groupName, group, path}, dragDrop} = this
  //   dragDrop.dropItem = {type: groupName, id: group, path}
  //   // Otherwise onDrop won't fire later
  //   e.preventDefault()
  // }

  // @action specCard_onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
  //   // Otherwise onDrop won't fire later
  //   e.preventDefault()
  //   // e.dataTransfer.effectAllowed should match with the dropEffect we will set otherwise on safari, drop event won't get fired
  //   // dropEffect doesn't work on ios Safari as of now
  //   // e.dataTransfer.dropEffect                         = 'link'
  //   const {props: {groupName, group, path}, dragDrop} = this
  //   dragDrop.dropItem = {type: groupName, id: group, path}
  // }

  // @action specCard_onDragLeave = () => {
  //   this.dragDrop.dropItem = null
  // }

  // @action specCard_onDragEnd = () => {
  //   this.dragDrop.dropItem = null
  // }

  // onSpecCardDrop = (e) => {
  //   const {category2ForImage} = this
  //   return category2ForImage || (dragDrop.dragItem?.dragType === 'product') ? undefined : this.specCard_onDrop(e.dataTransfer)
  // }

  // TODO - move into drag drop store
  // @action specCard_onDrop = async (dataTransfer) => {
  //   const {props: { info, info: {count}}, specStack} = this,
  //     {catalog: {boardConfig: {itemName}, libraryTypes, dragDrop, dragDrop: {dragItem}}} = this
  //   dragDrop.reset()

  //   if (!dragItem) { return}

  //   const {dragType} = dragItem

  //   const dragLibrary = libraryTypes[dragType] || {def: {displayName: {singular: 'item', plural: 'items'}}}

  //   if (dragType) {
  //     const name = 'nyi' //(!_.isEmpty(dragItem.label) && dragItem.label) || (!_.isEmpty(dragItem.name) && dragItem.name)

  //     this.confirmDialog = {
  //       dragItem: toJS(dragItem),
  //       title: `${dragItem.id == UNASSIGNED ? `Unassign all ${_.toLower(dragLibrary.def.displayName.plural)} from` : `Apply ${dragLibrary ? `${_.toLower(dragLibrary.def.displayName.singular)} ` : ''}'${_.truncate(name, {
  //         length: 50,
  //         omission: '...'
  //       })}' to`} the group '${info.title}'?\nThis change will affect ${count} ${_.lowerCase(itemName)}${count > 1 ? 's' : ''}.`,
  //       intent: bp.Intent.PRIMARY
  //     }
  //     const {confirmDialog: {title}, onConfirmAction, cancelConfirm} = this
  //     count == 1 || confirm(title) ? onConfirmAction() : cancelConfirm()
  //   } else {
  //     (event && event.preventDefault())
  //     const {
  //         props: {
  //           path,
  //           group
  //         },
  //         cardData
  //       } = this,
  //       {
  //         catalog: {
  //           massCreate
  //         }
  //       } = this

  //     this.busy = true
  //     this.dragDrop.dropItem = null
  //     if (!_.isEmpty(cardData)) {
  //       massCreate.spec = cardData
  //       massCreate.specStack = specStack
  //       massCreate.groupSpec = path
  //       massCreate.group = group
  //       let files = dataTransfer.files
  //       try {
  //         if (files && files.length === 0) {
  //           const object = dataTransfer.getData('text/html')
  //           if (object) {
  //             const image = await utils.getImageUrlFromLink(object)
  //             files = [image]
  //           } else {
  //             throw new Error('Image not found')
  //           }
  //         }
  //       } catch (error) {
  //         this.reset()
  //       }
  //       await massCreate.createProductWithImages(files)
  //       massCreate.productCount = 1
  //       massCreate.spec = null
  //       this.reset()
  //     }
  //   }
  // }

  @action cancelConfirm = () => {
    this.busy = false
    this.confirmDialog = null
    const {dragDrop} = this
    dragDrop.dropItem = null
    dragDrop.dropProduct = []
  }

  @action reset = () => {
    this.busy = false
    this.confirmDialog = null
    const {dragDrop} = this
    dragDrop.dropItem = null
    dragDrop.dropProduct = []
  }

  @action onConfirmAction = async () => {
    const {
        grouping, es, props: {path, groupName, info: {}}, childGroupStore: {productQueryVariables},
        confirmDialog: {dragItem}, isExpanded, storeNodeId
      } = this,
      {
        catalog: {
          refetch,
          index, catalogConfig: {
            dragType,
            dragType: {
              entityType, path: fieldPath, index: fromIndex,
              source, field, queryField
            }
          }, workspaceConfig: {_id}
        }
      } = this
    let match = {}
    if (queryField) {
      match[queryField] = dragItem.id
    } else {
      match = {
        id: dragItem.id
      }
    }
    this.busy = true

    console.log(`Todo - remove dragType from config/move fields into library configuration`, {dragItem, dragType})
    try {
      await es.assignToField({
        workspaceId: _id,
        pathFromSource: fieldPath,
        entityType: entityType,
        unassign: dragItem.id == 'unassigned',
        from: {
          filter: {
            query: {
              match
            },
            _source: source
          }, index: fromIndex
        },
        to: {
          field: field,
          filter: productQueryVariables.query
          , index: index
        }
      })

      const {dragDropState, ddHandler} = this
      dragDropState.status = 'success'
      ddHandler.clearStatusDelayed()
      refetch('catalogItems')

      // YFTS-2097 After dropping a spec on a group that is of another type of the same spec we need to maintain expanded state with the newly grouped spec
      if (isExpanded && dragItem.dragType == groupName) {
        grouping.setExpanded(dragItem.id, true, path)
      }
    } catch (err) {
      onSiteError(err)
      this.reset()
    } finally {
      this.reset()
    }
  }

  /**
   * Information specifically identifying this group - ie Plan = Apparel, Folder = Unassigned
   */
  get info() {
    return this.props.info
  }

  get isUnassigned() {
    return this.info.isUnassignedGroup
  }

  private setInfoCardRef = r => this.infoCardRef = r

  node: HTMLElement

  @computed get buttonToolbarProps(): any {
    const {
      props: {groupName, path, level, info, group, hideChildren}, catalog,
      isExpanded, grouping, isUnassigned, specStack, storeNode, levelOfDetail
    } = this

    const lod = this.catalog?.settings?.levelOfDetail ? catalog?.settings?.levelOfDetail : levelOfDetail
    return {
      storeNode,
      groupStore: this,
      levelOfDetail: lod,
      groupName,
      path, isExpanded, hideChildren, grouping, level, info, group, specStack, isUnassigned
    }
  }

  // region Inline Group Search

  @observable isGroupSearch = false
  @action toggleGroupSearch = () => this.isGroupSearch = !this.isGroupSearch

  // endregion

  // region Perspective Counts

  countForPerspective = (p: CatalogPerspectiveConfig) => {
    const {catalog, catalog: {filter}} = this
    if (p.id == catalog.workspaceConfig._id) {
      return (filter.onlyShowOfflineItems || catalog.settings.showOnlyOfflineItems) ? 0 : this.props.info.count
    } else {
      const {props: {info}} = this
      // Note that with composite queries we can't do another level of agg filter to get a given count for a where clause -
      // todo - revisit after https://github.com/elastic/elasticsearch/pull/38863#issuecomment-799719467 is merged
      return info.otherCounts?.[p.id] ?? this.props.info?.count ?? 0
    }
  }

  resetGroups = (storeNode) => {
    const {catalog: {settings: {perItem}}} = this,
      {offlineItems} = this

    delete storeNode.showOnlyOfflineItems
    delete storeNode.showOnlyOnlineItems
    offlineItems.forEach(({_id}) => delete perItem[_id])
  }

  resetAllGroups = () => {
    const {grouping} = this
    grouping?.nodes?.forEach((value) => this.resetGroups(value))
  }

  deSelectGroupItems = () => {
    const {catalog: {selection: {selectedIds}}} = this
    this.resetAllGroups()
    selectedIds.clear()
  }

  selectGroupItems = (offline) => {
    const {catalog, catalog: {selection: {selectedIds}, settings: {perItem}}} = this,
      {offlineItems, itemsIdArray, storeNode, settings} = this
    this.deSelectGroupItems()
    if (offline) {
      if (catalog.settings.showOnlyOfflineItems && !settings.showOnlyOfflineItems) {
        this.updateSettings({showOnlyOfflineItems: settings.showOnlyOfflineItems})
        catalog.settings.showOnlyOfflineItems = false
      } else {
        this.updateSettings({showOnlyOfflineItems: !settings.showOnlyOfflineItems})
      }

      this.updateSettings({showOnlyOnlineItems: false})
      storeNode.store.expand()

      offlineItems.forEach(({_id}) => perItem[_id] = Object.assign({}, perItem[_id], {lod: settings.showOnlyOfflineItems ? 1 : 0}))
      itemsIdArray?.filter(i => i.startsWith(OFFLINE_ID)).forEach(i => _.debounce(() => selectedIds.add(i), 100)())
    } else {
      this.updateSettings({showOnlyOfflineItems: false, showOnlyOnlineItems: !settings.showOnlyOnlineItems})
      catalog.settings.showOnlyOfflineItems = false
      itemsIdArray?.filter(i => !i.startsWith(OFFLINE_ID)).forEach(i => _.debounce(() => selectedIds.add(i), 100)())
    }
  }

  onClickPerspectiveCountButton = ({offline, perspective}: { offline: boolean, perspective: CatalogPerspectiveConfig }) => e => {
    const {catalog, selectGroupItems, settings, settings: {showOnlyOfflineItems, perspective: selectedPerspective}} = this
    if (offline) {
      this.updateSettings({showOnlyOfflineItems: !showOnlyOfflineItems})
    } else if (selectedPerspective != perspective.id) {
      this.updateSettings({perspective: perspective.id})
    } else {
      if (perspective.id === catalog.workspaceConfig._id) {
        this.updateSettings({perspective: undefined})
      }
    }
    //{? deSelectGroupItems() : selectGroupItems(offline)}
  }

  @computed get pathValue() {
    const {strings, props: {info: {keys}, path, group}} = this, {groupBy: {hierarchy, hierarchyGroups}, catalog: {libraries}} = this
    return path.reduce((acc, v, i) => {
      const field = hierarchy[i],
        hg = hierarchyGroups[i],
        json = JSON.parse(v)

      return _.assign(
        acc,
        {
          [field]: !v ? null
                      : v === strings.noValue
                        ? ''
                        : _.some(libraries, (spec) => spec?.name === field)
                          ? {name: json[field]}
                          : json[field]
        },
        hg.otherGroupedFields.reduce(
          (otherAcc, f) => _.assign(otherAcc, {
            [f.field]: _.some(libraries, (spec) => spec?.name === f.field)
                       ? {name: json[f.field]}
                       : json[f.field]
          }), {}))
    }, {})
  }

  @computed get keysIncludingPath() {
    const {props: {info: {keys}, path, group}} = this, {groupBy: {hierarchy}} = this
    return {
      ...this.pathValue,
      ...keys
    }
  }

  @action onCreateGroupCatalogItem = (e: React.MouseEvent) => this.createGroupCatalogItem()

  @action assignOfflineProperties = async (data = {}) => {
    const {es, vlp, stores, props: {info: {aggs, keys}, group}, grouping: {fieldName, esIndex, associatedLibrary}, keysIncludingPath, parentStore,
      catalog, catalog: {seasonSelectorFields, findFieldInTree, isFieldListType, c8hierarchy: c8h}} = this

    // console.log(`assignOfflineProperties`, {groupName, data})
    if (parentStore) {
      await parentStore.assignOfflineProperties(data)
    }

    if (esIndex && aggs?.ids?.buckets[0]?.key && group !== NULL_KEY_VALUE && group !== EMPTY_STRING_VALUE) {
      const specData = await es.get({index: esIndex, id: aggs?.ids?.buckets[0]?.key})
      _.set(data, fieldName, specData)
    }

    _.keys(keys).forEach(k => {
      if (!data[k]) {
        const vlpHierarchy = vlp?.hierarchy?.hasVlpHierarchy && vlp.hierarchy.parseHierarchyGroup(k)
        const field = findFieldInTree(k)
        if (vlpHierarchy) {
          // Add the plan
          //const plan = isFolder ?
          const v = keys[k],
            {type} = vlpHierarchy,
            {hierarchy: {itemsFor, folderPathToRoot, vlpDataFieldNames}} = vlp

          if (v) {
            let item = itemsFor(type, v, keysIncludingPath)

            if (!item) {
              debugger
            }

            if (item?.folder) {
              data['subCategories'] = folderPathToRoot(item.folder)
              data['plan'] = _.pick(item.plan, vlpDataFieldNames)
            }
            if (item?.plan) {
              data['plan'] = _.pick(item?.plan, vlpDataFieldNames)
            }

            if (item?.collection) {
              data['collection'] = _.pick(item?.collection, vlpDataFieldNames)
              data['subCategories'] = folderPathToRoot(item.collection.folder)
            }
          }
        } else if (c8h?.hierarchies[k]) {
          const {hierarchies: {[k]: items}} = c8h,
            matches = items.filter(v => v.name === keys[k])

          if (!_.isEmpty(matches)) {
            _.set(data, catalog.esFieldFor_byField(k), _.first(matches))
          } else {
            catalog.onError(new Error(`Unable to find matching hierarchy for ${k} - ${keys[k]}`))
          }
        }
        else {
          const isListType = isFieldListType(field?.def?.type)
          _.set(data, catalog.esFieldFor_byField(k), isListType ? [keysIncludingPath[k]] : keysIncludingPath[k])
        }
      }
    })

    if (this.groupDefinition?.onAssign) {
      data = await this.groupDefinition.onAssign(data, {store: this, stores})
    }

    return data
  }

  @action createGroupCatalogItem = async (data = {}) => {
    await this.assignOfflineProperties(data)

    return await this.catalog.createItem.createOfflineItem({data})
  }

  // endregion

  @observable creatingItem = false

  // This code is no longer called...
  @action onCreateItem = async () => {
    await this.createGroupCatalogItem()
  }
  @action onCreateItems = async () => {
    console.warn(`nyi`)
  }

  @action onCreateItemFromImages = async () => {
    console.warn(`nyi`)
  }

  toggleExpandDueToMouseOverTimeout: number
  onExpandButtonDragLeave = () => {
    clearTimeout(this.toggleExpandDueToMouseOverTimeout)
  }

  onExpandButtonDragEnter = () => {
    const {dragDrop} = this
    if (!this.isExpanded && dragDrop.dragOverItem?.id === this.storeNodeId) {
      // Don't immediately do it as it's jarring, make sure the user stays moused over this group for a bit
      // Todo - Only expand the group if we have a drop action that can happen INSIDE the group
      this.toggleExpandDueToMouseOverTimeout = window.setTimeout(() => {
        if (!this.isExpanded && dragDrop.dragOverItem?.id === this.storeNodeId) {
          this.toggleExpanded()
        }
      }, 400)

      // this.expandedDueToDragOver = true
      // when(() => !dragDrop.isDragging, () => this.expandedDueToDragOver = false)
      // when(() => {
      //   const {isDragging, dragOverItem} = dragDrop, {storeNode, storeNodeId} = this
      //   return !isDragging || (dragOverItem?.id && dragOverItem?.id != storeNodeId && !storeNode?.productStore?.itemIdSet?.has(dragOverItem.id))
      // }, () => {
      //   // Avoid collapsing instantly as it'll move the object under the user's mouse
      //   setTimeout(() => this.expandedDueToDragOver = false, 500)
      // })
    }
  }

  @computed get offlineItems() {
    if (this.filter.noShowOfflineItems) return []
    return _.chain(this.props.info.offlineItems ?? []).orderBy('creationDate', 'desc').value()
  }

  @computed get catalogItems() {
    return [
      ...Array.from(this.catalogItemsByGroup.values()),
      //bug/YFTS-8`76 - quick fix - circular data in computed
      // ..._.flatMap(this.childGroupStore?.childGroups?.map(cg => cg?.store?.itemWrappersIncludingChildren ?? []) ?? [])
    ].flat()
  }

  @computed get builderCardStores() : Array<BuilderCardStore> {
    const {catalog: {builderCardStoreFor, idFor, catalogItems}, queryKey} = this
    return catalogItems.map(c => builderCardStoreFor(`${queryKey}${CATALOG_ID_SEPARATOR}${idFor(c)}`)).filter(_.identity)
  }

  @computed get builderCardsByLOD() {
    return _.groupBy(this.builderCardStores, s => s.levelOfDetail)
  }

  @computed get catalogItemByID() {
    return _.keyBy(this.catalogItems, v => this.catalog.idFor(v))
  }

  @action onCollapseAllLevelOfDetail = () => {
    this.settings.levelOfDetail = 0
    const {catalogItemByID, catalog: {settings: {perItem}}, queryKey} = this
    _.keys(catalogItemByID).forEach(id => {
      const item = perItem[`${queryKey}.${id}`]

      if (item?.lod === 1) {
        item.lod = 0
      }
    })
  }

  @computed get hasInlineCollapsedCatalogItems() {
    const {settings: {levelOfDetail}, catalogItemByID, catalog: {settings: {perItem}}, queryKey} = this
    return _.keys(catalogItemByID).some(id => perItem[`${queryKey}.${id}`]?.lod === 0)
  }

  @computed get hasInlineExpandedCatalogItems() {
    const {settings: {levelOfDetail}, catalogItemByID, catalog: {settings: {perItem}}, queryKey} = this
    return _.keys(catalogItemByID).some(id => perItem[`${queryKey}.${id}`]?.lod === 1)
  }

  get forceShowInlineAggregationDetails() { return this.settings.forceShowInlineAggregationDetails}

  @action toggleShowInlineAggregationDetails = () => {
    this.updateSettings({forceShowInlineAggregationDetails: !this.forceShowInlineAggregationDetails})
  }

  @computed get showingInlineAggregationDetails() {
    const {catalog: {settings: {aggregateLocation, showAggDetailsWhenCollapsed, showAggs}}, isExpanded, showingAggregateDetails, forceShowInlineAggregationDetails, isSelected} = this

    if (!showAggs && aggregateLocation == 'inline' && forceShowInlineAggregationDetails) return true

    return showingAggregateDetails && aggregateLocation == 'inline' && (isExpanded || showAggDetailsWhenCollapsed || isSelected)
  }

  @computed get showingAggregateDetails() {
    const {catalog: {settings: {aggregateDetailsGroupPath, aggregateLocation, showAggs, showAggDetailsWhenCollapsed}}, isSelected} = this
    return aggregateLocation == 'inline' && this.forceShowInlineAggregationDetails
           ? true
           : !showAggs
             ? false
             : aggregateLocation == 'inline'
               ? (this.isExpanded || showAggDetailsWhenCollapsed || isSelected) && showAggs // || aggregateDetailsGroupPath.some(path =>_.isEqual(this.pathIncludingSelfKeys, path))
               : _.isEqual(this.pathIncludingSelfKeys, _.first(aggregateDetailsGroupPath))
  }

  @computed get showingAggregateDetailsinSidebar() {
    const {catalog: {settings: {aggregateDetailsGroupPath, aggregateLocation, showAggs, showAggDetailsWhenCollapsed}}} = this
    return !showAggs
           ? false
           : aggregateLocation == 'sidebar'
             ? _.isEqual(this.pathIncludingSelfKeys, _.last(aggregateDetailsGroupPath)) // || aggregateDetailsGroupPath.some(path =>_.isEqual(this.pathIncludingSelfKeys, path))
             : false
  }

  @action onToggleAggregateDetailsSidebar = () => {
    const {catalog, pathIncludingSelfKeys} = this,
      {settings, flexLayout, settings: {aggregateDetailsGroupPath, aggregateLocation}, aggregation: {toggleAggregationGroup}} = catalog

    if (_.isEqual(this.pathIncludingSelfKeys, _.last(aggregateDetailsGroupPath))) {
      settings.showAggs = true
      settings.aggregateLocation = aggregateLocation == 'sidebar' ? 'inline' : 'sidebar'
      if (settings.aggregateLocation === 'inline') {
        flexLayout.toggleKnownTab('catalogAggregateDetails', false)
      }
    } else {
      settings.aggregateLocation = 'sidebar'
      settings.showAggs = true
      toggleAggregationGroup(pathIncludingSelfKeys)
    }
  }

  @action onToggleAggregateDetails = () => {
    const {pathIncludingSelfKeys, catalog, stores: {sidebar: {right}}} = this

    catalog.aggregation.toggleAggregationGroup(pathIncludingSelfKeys)
    right.updateState({
      open: false,
      path: []
    })
  }

  ref: HTMLDivElement
  onSetRef = r => {
    this.ref = r
  }

  scrollIntoView = () => {
    this.ref?.scrollIntoViewIfNeeded()
  }

  @action onToggleFullHeight = () => {
    this.updateSettings({fullHeight: !this.settings.fullHeight})
  }

  @action onToggleExpandCollapse = () => {
    const {props: {info, path, group}, grouping, scrollToSpecAfterAnimationCompletes} = this

    runInAction(`toggleExpandCollapse(${info.id}, ${group})`, () => {
      //false && level > 0 ? groupBySpec.toggleExpanded('all', path) :
      grouping && grouping.toggleExpanded(group, path)
      scrollToSpecAfterAnimationCompletes()
    })
  }

  @action onToggleQrCode = () => {
    const {grouping, props: {path, group}} = this
    grouping && grouping.toggleShowQrCode(group, path)
  }

  @computed get showingSpecDetails() {
    const {libraryId, libraryWhere: where, stores: {sidebar: {left}}} = this
    return left.isShowingSpec({libraryId, where})
  }

  /**
   * What sidebar library should toggling details trigger?
   *
   * This is quite complex as we can be a regular group by merchProduct (MP library) or we could be grouped with a subtitle including season (which should trigger the MPV library)
   */
  @computed get library() {
    const {props: {groupName}, grouping: {compositeFields}, catalog: {libraries}} = this

    // Find the library that matches the largest # of our fields.  If there's a tie, default to the one that matches our group name
    const bestMatch = _.maxBy(
      libraries.map(
        l => ({
          library: l,
          score: l.name === groupName && l.renderer !== 'chip' ? (compositeFields.some(f => f == l.name) ? 1 : 0)
                                                                 + l.catalogFields?.filter(field => compositeFields.indexOf(field) != -1).length : 0
        })), l => l.score)
    return !bestMatch?.score ? null : bestMatch.library //libraryById[groupName] ? groupName : (boardConfig.libraries?.find(l => l.catalogFields?.find(f => f == groupName))?.name ?? groupName)
  }

  @computed get libraryId() {
    return this.library?.name
  }

  @computed get libraryWhere(): Array<{ field: string, value: any }> {
    const {library, props: {groupName, group, info}} = this
    if (info.keys && library?.catalogFields) {
      return library.catalogFields.map(k => ({field: k, value: info.keys[k]}))
    } else {
      return [{field: groupName, value: group}]
    }
  }

  // @computed get libraryItemId() {
  //   const {props: {groupName, group, info}} = this
  //   if (info.keys) {
  //     return info.keys[groupName]
  //   } else {
  //     return group
  //   }
  // }

  @action onToggleSpecDetails = () => {
    const {showingSpecDetails, libraryWhere, libraryId, props: {groupName}, stores: {sidebar: {left}}} = this

    if (showingSpecDetails) {
      left.open = false
    } else {
      left.updateState({tab: 0, open: true, path: [{libraryId: libraryId, where: libraryWhere, sidebarContext: 'xref', xref: groupName}]})
    }
  }

  @computed get showExpandCollapseAll() {
    const {props: {level}, isExpanded} = this, {groupBy: {hierarchy, pivotView}} = this
    return level + 1 < hierarchy.length && hierarchy.length > 1 && !pivotView
  }

  @action onCollapseAll = () => {
    const {props: {path, group, level}, grouping, isExpanded, catalog: {groupBy, groupBy: {hierarchyGroups}}} = this

    if (groupBy.settings.expandAllLevel > level) {
      groupBy.settings.expandAllLevel = Math.max(0, level - 1)
    }

    const childGroup = hierarchyGroups[level + 1]
    const itemsCollapsed = childGroup.collapseAllUnder([...path, group])
    if ((itemsCollapsed.length == 0) && isExpanded) {
      grouping.setExpanded(grouping.pathLookup(group, path), false)
    }

    // if (itemsCollapsed.length) {
    //   console.log(`collapsed children:  `, itemsCollapsed)
    // }
  }

  @computed get groupIndex() {
    const {grouping: {groupsInOrder}, storeNode} = this
    return groupsInOrder.indexOf(storeNode)
  }

  @computed get nextGroup() {
    const {grouping: {groupsInOrder}, groupIndex: index} = this,
      nextGroup = groupsInOrder[index + 1 == groupsInOrder.length ? 0 : index + 1]
    return nextGroup
  }

  @action onExpandAll = () => {
    // If we're not expanded, expand ourself and our children
    const {props: {path, group, level}, grouping, isExpanded, catalog: {groupBy: {hierarchyGroups}}} = this
    if (!isExpanded) {
      grouping.setExpanded(grouping.pathLookup(group, path), true)
    } else {
      const childGroup = hierarchyGroups[level + 1]
      childGroup.expandAllUnder([...path, group])
    }
  }

  @action onShowLevelOfDetail = () => {
    const {props: {path, group, level}, toggleLevelOfDetail, builderCardStores} = this,
      {catalog: {groupBy: {hierarchyGroups}}} = this
    const childGroup = hierarchyGroups[level + 1]
    if (childGroup) {
      childGroup.setLevelOfDetailAllUnder([...path, group], toggleLevelOfDetail, 1)
    }

    this.settings.levelOfDetail = 1
    builderCardStores.forEach(s => {
      if (s.localLOD === 0) {
        s.localLOD = null
      }
    })
  }

  selectImagesFileInput: HTMLInputElement = null
  onSetSelectImagesFileInputRef = ref => this.selectImagesFileInput = ref
  @action onSelectImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {props: {group, path}, specStack} = this,
      {catalog: {massCreate}} = this
    const target = event.target

    if (target.files.length > 0) {
      massCreate.specStack = specStack
      massCreate.groupSpec = path
      massCreate.group = group
      // massCreate.createProductWithImages(target.files, createProductInput, null, gqlApi.whereProductsClause).then(() => {
      //   massCreate.productCount = 1
      // })
    }
  }

  @computed get hasToolbarPopover() {
    const {isExpanded, isFlat, grouping: {isLeaf}, groupBy: {settings: {columnView}}} = this
    return isFlat
    //return (columnView && isFlat) || (isFlat && !columnView && columns > 1 && (!isExpanded || isLeaf))
  }

  /**
   * For asics we want to copy various attributes off of an existing MPV when creating or assigning to a new MPV when changing groups
   */
  getFirstCatalogItem = async () => {
    const {catalog, es, childGroupStore: {productQueryVariables}} = this

    const {items} = es.unwrapHitsResult(await es.search({
      body: {
        ...productQueryVariables,
        size: 1
      },
      index: catalog.index
    }))

    return _.first(items)
  }

  /**
   * Any sort of object within a group on which having the mouse should cause other items in the group to change their render behavior
   * For example, YFTS-7902
   */
  @observable.ref _contextualHoverItem: ContextualGroupHoverItem

  @computed get contextualHoverItemId() { return this.catalog.idFor(this.contextualHoverItem?.item) }

  @computed get contextualHoverItem(): ContextualGroupHoverItem {
    return this._contextualHoverItem ?? this.parentStore?.contextualHoverItem
  }

  set contextualHoverItem(v: ContextualGroupHoverItem) {
    this._contextualHoverItem = v
  }

  /**
   * The catalog items (builder cards) matching the moused over item from group swatches or subgroup
   * {@see BuilderCardStore} {@see GroupSwatches} {@see GroupByGroupHeroCards}
   */
  @computed get matchingCatalogItemsForSwatchHoverSelect(): Set<string> {
    const {catalog, contextualHoverItem, swatchStore, storeNode: {productStore}, pathLookup} = this
    if (!contextualHoverItem && !swatchStore?.selectedSwatches?.size) return new Set<string>()

    if (this.hasChildGroup) {
      return new Set<string>(this.childGroupStore.childGroups.reduce((acc, cg) => [...acc, ...Array.from(cg?.store?.matchingCatalogItemsForSwatchHoverSelect.keys()) ?? []], []))
    } else {
      const {catalog: {idFor}} = this,
        {catalogItemsArray: catalogItems} = productStore ?? {catalogItemsArray: []},
        toMatch = _.uniqBy([...Array.from(swatchStore?.selectedSwatches.values() ?? []), contextualHoverItem].filter(_.identity), v => catalog.idFor(v.item))

      const r = new Set<string>(
        _.flatMap(
          toMatch.map(({item, field}) => {
            if (field) {
              const esField = catalog.esFieldFor(field),
                matchId = idFor(item),
                r = catalogItems.filter(item => {
                  const specValue = _.get(item, esField)
                  if (_.isArray(specValue)) {
                    return specValue.some(v => idFor(v) === matchId)
                  } else {
                    return idFor(specValue) === matchId
                  }
                })

              //return r.reduce((acc, k) => _.assign(acc, {[`${pathLookup}.${idFor(item)}`]: item}), {})
              return r.map(item => `${pathLookup}.${idFor(item)}`)
            }
            return []
          })))

      return r
    }
  }

  @action selectContextualHoverCatalogItems = (e: React.MouseEvent) => {
    const {matchingCatalogItemsForSwatchHoverSelect, catalog} = this

    if (!_.isEmpty(matchingCatalogItemsForSwatchHoverSelect)) {
      const ids = Array.from(matchingCatalogItemsForSwatchHoverSelect.keys())
      const {selection: {selectedIds}} = catalog

      if (!e.shiftKey) {
        selectedIds.replace(ids)
      } else {
        ids.forEach(id => selectedIds.has(id) ? selectedIds.delete(id) : selectedIds.add(id))
      }
    }
  }

  /** The current BuilderCard being moused over within this group **/
  @observable.ref private _contextualHoverCatalogItem: BuilderCardStore

  @computed get contextualHoverCatalogItem() {
    return this._contextualHoverCatalogItem || this.parentStore?.contextualHoverCatalogItem
  }

  @action setContextualHoverCatalogItem = (v) => {
    this._contextualHoverCatalogItem = v
    if (this.parentStore) {
      this.parentStore.setContextualHoverCatalogItem(v)
    }
  }

  @computed get selectedCatalogItemsInGroup() {
    const {catalogItemByID, catalog} = this,
      {selection: {selectedIds}, selectAll, parseIdFromPath} = catalog

    if (_.isEmpty(catalogItemByID)) {
      return []
    } else if (selectAll) {
      return _.values(catalogItemByID)
    } else {
      return Array.from(selectedIds.keys()).map(id => catalogItemByID[parseIdFromPath(id)]).filter(_.identity)
    }
  }

  @computed get hasSwatches() {
    return this.swatchStore.hoverItem
  }

  matchesContextualCatalogItem = computedFn((data: any, field: string) => {
    const {contextualHoverCatalogItem, selectedCatalogItemsInGroup, catalog} = this

    // console.log(this.swatchStore?.hoverItem)
    // if (!this.swatchStore?.hoverItem) return undefined

    if (!data || (!contextualHoverCatalogItem && _.isEmpty(selectedCatalogItemsInGroup))) return false

    const esField = catalog.esFieldFor_byField(field), {idFor} = catalog,
      dataId = idFor(data)

    if (contextualHoverCatalogItem?.data) {
      const item = _.get(contextualHoverCatalogItem.data, esField)
      if (_.isArray(item)) {
        if (item.some(v => idFor(v) === dataId)) return true
      } else {
        const contextId = idFor(item)
        if (contextId == dataId) return true
      }
    }

    const r = selectedCatalogItemsInGroup.some(data => {
      const item = _.get(data, esField)
      if (_.isArray(item)) {
        return item.some(v => idFor(v) === dataId)
      } else {
        const contextId = idFor(item)
        return (contextId == dataId)
      }
    })

    return r
  })

  @action onContextMenu = (e: React.MouseEvent) => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  @computed get filterMeTerm() {
    return {
      bool: {
        must_not: this.childGroupStore.productQueryVariables.query
      }
    }
  }

  @computed get isThisGroupExplicitlyExcluded() {
    const {filter: {state: {extraTerms}},pathLookup: id} = this

    const term = (extraTerms ?? []).find(t => t.id == id)

    if (term) {
      if (!term.store) {
        setTimeout(() => term.store = this, 0)
      }
      return true
    }

    return false
  }

  @action toggleExcludeThisGroupCombination = () => {
    const {filter: {state}, isThisGroupExplicitlyExcluded, filterMeTerm, pathLookup: id} = this

    if (isThisGroupExplicitlyExcluded) {
      state.extraTerms = state.extraTerms.filter(t => t.id !== id)
    } else {
      state.extraTerms.push({id, store: this, query: filterMeTerm})
    }
  }

  @action navigateTo = (field: string) => {
    const {catalog, props: {info}, grouping: {mySubtitleFields}, valueForSubtitleField} = this,
      {vlp: {hierarchy: {parseHierarchyGroup, itemOfType}}} = this,
      {type} = parseHierarchyGroup(field) ?? {}

    if (!type) return

    const id = info.keys[field],
      item = itemOfType(type, id, {}),
      value = valueForSubtitleField(mySubtitleFields.find(f => f.field === field))
    // console.log({id, field, item, type, value, info})

    if (!_.isEmpty(item)) {
      catalog.c8hierarchy.navigateTo({type, ...item} as any)
    } else if (value?.item) {
      catalog.c8hierarchy.navigateTo({type, ...value.item})
    }

    //catalog.c8hierarchy.navigateTo({})
  }

  valueForSubtitleField = (field: GroupTitleConfig): { item: any, label: string } => {
    if (!field) return {item: null, label: this.strings.noValue}

    const {props: {info, info: {keys}}} = this
    return field.valueFor ? field.valueFor(info) : keys ? {item: keys[field.field], label: keys[field.field]} : {item: null, label: this.strings.noValue}
  }

  @computed get hasSwatchHoverContext() {
    return this.swatchStore?.hoverItem && (this.contextualHoverItem != null || this.swatchStore?.selectedSwatches?.size != 0)
  }
  matchesGroupHoverItem = (idWithPath: string) => {
    const {matchingCatalogItemsForSwatchHoverSelect, contextualHoverItem, groupBy} = this
    return matchingCatalogItemsForSwatchHoverSelect.has(idWithPath) ? true : contextualHoverItem ? false : groupBy.settings.hideCatalogItemsOnSwatchHover ? false : undefined
  }

  findMatchingStoreInHierarchyForField = (field: ICatalogFieldConfig) : GroupByGroupStore => {
    const {parentStore, props: {info: {keys}}} = this
    if (keys[field.field]) return this
    if (!parentStore) return null
    return parentStore.findMatchingStoreInHierarchyForField(field)
  }
}




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupByGroupStore.tsx